"use client";

import { Button } from "@/components/ui";
import { ArrowRight, Search, ShieldCheck } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type HowItWorksSectionProps = {
  className?: string;
  showCtas?: boolean;
};

const STEPS = [
  {
    label: "Step 1 - The Audit",
    heading: "See what AI thinks of you right\u00a0now",
    body: "We scan your website to see exactly what ChatGPT and Gemini see and reveal why they might be ignoring\u00a0you.",
  },
  {
    label: "Step 2 - The Optimization",
    heading: "Layer on AI\u2011ready\u00a0upgrades",
    body: "Schema markup, BLUF\u2011format rewrites, auto\u2011generated FAQs, and an LLMs.txt file\u2009\u2014\u2009laid out in an AI view of each page, a public page linked from your footer. Your design stays exactly as\u00a0it\u00a0is.",
  },
  {
    label: "Step 3 - The Recommendation",
    heading: "Get recommended when it\u00a0matters",
    body: "As AI tools start trusting your data, they stop ignoring you and start citing you with a direct link back to\u00a0you.",
  },
  {
    label: "Step 4 - The Result",
    heading: "See how often AI mentions you and how it\u2019s\u00a0growing",
    body: "Track which AI models talk about you and how much traffic they\u00a0bring.",
  },
];

function Card1() {
  return (
    <div
      aria-hidden="true"
      className="bg-white rounded-2xl p-5 lg:p-6 border border-black/[0.06] shadow-[0_2px_10px_rgba(16,24,40,0.05)]"
    >
      <div className="flex items-center gap-2 mb-4">
        <Search className="w-5 h-5 text-nws-blue" />
        <span className="text-sm font-semibold text-black/70">
          AI Visibility Report
        </span>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-black/50">Schema Markup</span>
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-nws-blue/10 rounded-full overflow-hidden">
              <div className="w-1/4 h-full bg-nws-blue/40 rounded-full" />
            </div>
            <span className="text-[11px] text-nws-blue font-medium w-12 text-right">
              Missing
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-black/50">FAQ Structure</span>
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-nws-blue/10 rounded-full overflow-hidden">
              <div className="w-1/2 h-full bg-nws-blue/60 rounded-full" />
            </div>
            <span className="text-[11px] text-nws-blue font-medium w-12 text-right">
              Partial
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-black/50">AI Readability</span>
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-nws-blue/10 rounded-full overflow-hidden">
              <div className="w-1/3 h-full bg-nws-blue/40 rounded-full" />
            </div>
            <span className="text-[11px] text-nws-blue font-medium w-12 text-right">
              Low
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card2() {
  const optimizations = [
    {
      label: "JSON-LD Schema",
      code: '{"@type": "LocalBusiness", ...}',
      color: "bg-nws-blue",
      textColor: "text-white",
    },
    {
      label: "BLUF Format",
      code: "Bottom line up front for AI",
      color: "bg-nws-blue/10",
      textColor: "text-nws-blue",
    },
    {
      label: "Auto FAQs",
      code: "Schema-ready Q&A from your content",
      color: "bg-nws-blue/10",
      textColor: "text-nws-blue",
    },
    {
      label: "LLMs.txt",
      code: "Roadmap for AI crawlers",
      color: "bg-black",
      textColor: "text-white",
    },
  ];

  return (
    <div
      aria-hidden="true"
      className="bg-white rounded-2xl p-5 lg:p-6 border border-black/[0.06] shadow-[0_2px_10px_rgba(16,24,40,0.05)]"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-5 h-5 rounded-full bg-nws-blue flex items-center justify-center">
          <span className="text-[8px] text-white">&#129302;</span>
        </div>
        <span className="text-[11px] text-nws-blue font-semibold">
          Added to AI view only
        </span>
        <span className="ml-auto text-[10px] text-black/30 font-medium">
          Your site stays the same
        </span>
      </div>
      <div className="space-y-2">
        {optimizations.map((opt) => (
          <div
            key={opt.label}
            className="flex items-center gap-3 rounded-lg border border-black/5 p-2.5"
          >
            <span
              className={`${opt.color} ${opt.textColor} text-[10px] font-bold px-2 py-1 rounded-md whitespace-nowrap`}
            >
              {opt.label}
            </span>
            <span className="text-[10px] text-black/50 font-mono truncate">
              {opt.code}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Card3() {
  return (
    <div
      aria-hidden="true"
      className="bg-white rounded-2xl p-5 lg:p-6 border border-black/[0.06] shadow-[0_2px_10px_rgba(16,24,40,0.05)]"
    >
      <div className="flex gap-3">
        <Image
          src="/mobile/openai-icon.svg"
          alt="ChatGPT"
          width={28}
          height={28}
          className="w-7 h-7 flex-shrink-0"
        />
        <div className="flex-1 space-y-2.5">
          <div className="h-2.5 bg-black/10 rounded w-full" />
          <div className="h-2.5 bg-black/5 rounded w-4/5" />
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-xs text-black/40">I recommend</span>
            <span className="text-xs font-semibold text-nws-blue bg-nws-blue/10 px-2 py-0.5 rounded">
              Your Business
            </span>
          </div>
          <div className="flex items-center gap-1 pt-2.5 border-t border-black/5">
            <span className="text-[10px] text-nws-blue">[1]</span>
            <span className="text-[10px] text-nws-blue underline font-medium">
              Book Appointment
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card4() {
  return (
    <div
      aria-hidden="true"
      className="bg-white rounded-2xl p-5 lg:p-6 border border-black/[0.06] shadow-[0_2px_10px_rgba(16,24,40,0.05)]"
    >
      <div className="relative h-[200px] lg:h-[220px] w-full">
        <div className="absolute left-0 top-0 bottom-6 w-12 flex flex-col justify-between text-right pr-2">
          <span className="font-sans text-xs text-gray-400">+300%</span>
          <span className="font-sans text-xs text-gray-400">+200%</span>
          <span className="font-sans text-xs text-gray-400">+100%</span>
          <span className="font-sans text-xs text-gray-400">0%</span>
        </div>
        <div className="absolute left-12 right-0 top-0 bottom-6">
          <div className="absolute inset-0 flex flex-col justify-between">
            <div className="border-b border-gray-100 w-full" />
            <div className="border-b border-gray-100 w-full" />
            <div className="border-b border-gray-100 w-full" />
            <div className="border-b border-gray-200 w-full" />
          </div>
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 300 140"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient
                id="hiw4TrafficGrad"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#AED1FF" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#AED1FF" stopOpacity="0.05" />
              </linearGradient>
              <linearGradient
                id="hiw4MentionsGrad"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            <path
              d="M0,130 C30,125 60,115 100,90 C140,65 180,35 220,18 C250,8 280,2 300,0 L300,140 L0,140 Z"
              fill="url(#hiw4MentionsGrad)"
            />
            <path
              d="M0,140 C50,140 100,140 140,135 C180,125 220,110 260,95 C280,92 295,90 300,90 L300,140 L0,140 Z"
              fill="url(#hiw4TrafficGrad)"
            />
            <path
              d="M0,130 C30,125 60,115 100,90 C140,65 180,35 220,18 C250,8 280,2 300,0"
              fill="none"
              stroke="#3B82F6"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M0,140 C50,140 100,140 140,135 C180,125 220,110 260,95 C280,92 295,90 300,90"
              fill="none"
              stroke="#AED1FF"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute top-[8%] right-8 bg-[#3B82F6] text-white text-xs font-semibold px-2 py-1 rounded-md shadow-sm whitespace-nowrap">
            AI Mentions
          </div>
          <div className="absolute top-[62%] right-2 bg-[#AED1FF] text-black text-xs font-semibold px-2 py-1 rounded-md shadow-sm whitespace-nowrap">
            AI Visitors
          </div>
        </div>
        <div className="absolute left-12 right-0 bottom-0 flex justify-between px-1">
          <span className="font-sans text-xs text-gray-500">Week 1</span>
          <span className="font-sans text-xs text-gray-500">Week 5</span>
          <span className="font-sans text-xs text-gray-500">Week 10</span>
        </div>
      </div>
    </div>
  );
}

const CARDS = [
  <Card1 key={0} />,
  <Card2 key={1} />,
  <Card3 key={2} />,
  <Card4 key={3} />,
];

export default function HowItWorksSection({
  className,
  showCtas = true,
}: HowItWorksSectionProps) {
  const scrollRef = useRef(null as any);
  const [activeStep, setActiveStep] = useState(0);
  const isClickScrolling = useRef(false);

  useEffect(() => {
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) return; // already scheduled for next frame
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (isClickScrolling.current) return;
        const el = scrollRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const scrolled = Math.max(0, -rect.top);
        const totalScrollable = rect.height - window.innerHeight;
        if (totalScrollable <= 0) return;
        const progress = Math.min(0.9999, scrolled / totalScrollable);
        const newStep = Math.min(3, Math.floor(progress * 4));
        setActiveStep((prev) => (prev === newStep ? prev : newStep));
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  const goToStep = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    // Activate immediately - CSS transitions handle the visual change
    setActiveStep(i);
    isClickScrolling.current = true;
    const totalScrollable = el.offsetHeight - window.innerHeight;
    // Jump scroll position instantly to midpoint of the step's range
    // so continued manual scrolling picks up from the right place.
    // No smooth scroll - the 500ms CSS transition IS the animation.
    const target = el.offsetTop + (totalScrollable * (i + 0.5)) / 4;
    window.scrollTo({ top: target, behavior: "instant" as ScrollBehavior });
    setTimeout(() => {
      isClickScrolling.current = false;
    }, 100);
  };

  return (
    <div
      id="how-it-works"
      className={cn("flex flex-col w-full scroll-mt-28 lg:scroll-mt-32", className)}
    >
      {/* Mobile header - shown only below lg */}
      <div className="lg:hidden w-full max-w-[520px] mx-auto px-6 pt-10 pb-8">
        <h2 className="font-sans font-medium text-[30px] lg:text-[40px] text-[#15151a] text-center tracking-[-0.03em] leading-[1.1]">
          How we help AI understand and recommend your business
        </h2>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-5 text-[13px] text-black/55 font-sans">
          <span className="flex items-center gap-2">
            <svg
              className="h-4 w-4 text-green-500 shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Zero maintenance
          </span>
          <span className="flex items-center gap-2">
            <svg
              className="h-4 w-4 text-green-500 shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            100% automated
          </span>
        </div>
      </div>

      {/* DESKTOP: sticky scroll */}
      <div
        ref={scrollRef}
        className="hidden lg:block relative"
        style={{ height: "400vh" }}
      >
        <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
          {/* Headline always visible inside sticky panel */}
          <div className="w-full max-w-[1080px] mx-auto px-6 pb-8 pt-10">
            <h2 className="font-sans font-medium text-[30px] lg:text-[40px] text-[#15151a] text-center tracking-[-0.03em] leading-[1.1]">
              How we help AI understand and recommend your business
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-5 text-[15px] text-black/55 font-sans">
              <span className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 text-green-500 shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Zero maintenance
              </span>
              <span className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 text-green-500 shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                100% automated
              </span>
            </div>
          </div>
          <div className="w-full max-w-[1080px] mx-auto px-6 flex items-center gap-16">
            {/* Left: step list */}
            <div className="flex-1 flex flex-col gap-0">
              {STEPS.map((step, i) => {
                const isActive = i === activeStep;
                return (
                  <div key={i} className="flex gap-4">
                    {/* Timeline line + dot */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => goToStep(i)}
                        aria-label={`Go to ${step.label}`}
                        className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center font-semibold text-base shrink-0 transition-all duration-500 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6]",
                          isActive
                            ? "bg-[#3B82F6] text-white shadow-md shadow-blue-200"
                            : "bg-black/8 text-black/30 hover:bg-black/15"
                        )}
                      >
                        {i + 1}
                      </button>
                      {i < 3 && (
                        <div
                          className={cn(
                            "w-px flex-1 min-h-[32px] transition-all duration-500",
                            i < activeStep ? "bg-[#3B82F6]/40" : "bg-black/10"
                          )}
                        />
                      )}
                    </div>

                    {/* Text */}
                    <div className="flex-1 pb-8">
                      <button
                        onClick={() => goToStep(i)}
                        className="text-left w-full focus:outline-none"
                      >
                        <h3
                          className={cn(
                            "font-sans font-medium text-lg xl:text-xl text-black tracking-[-0.03em] leading-[130%] mt-2 mb-2 transition-all duration-500",
                            isActive
                              ? "opacity-100"
                              : "opacity-35 hover:opacity-60"
                          )}
                        >
                          {step.heading}
                        </h3>
                      </button>
                      <div
                        className={cn(
                          "overflow-hidden transition-all duration-500",
                          isActive
                            ? "max-h-[300px] opacity-100"
                            : "max-h-0 opacity-0"
                        )}
                      >
                        <p className="font-sans text-base text-black/60 leading-relaxed max-w-[420px]">
                          {step.body}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right: card crossfade */}
            <div className="flex-1 relative" style={{ height: "320px" }}>
              {CARDS.map((card, i) => (
                <div
                  key={i}
                  className={cn(
                    "absolute inset-0 transition-all duration-500 ease-in-out",
                    i === activeStep
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 translate-y-4 pointer-events-none"
                  )}
                >
                  {card}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE: normal stacked sections */}
      <div className="lg:hidden flex flex-col">
        {STEPS.map((step, i) => (
          <section key={i} className="w-full max-w-[520px] mx-auto px-6 pt-16 pb-6">
            <div className="flex gap-3 items-start mb-3">
              <div className="w-8 h-8 rounded-full bg-[#3B82F6] text-white flex items-center justify-center font-semibold text-sm shrink-0 mt-0.5">
                {i + 1}
              </div>
              <h3 className="font-sans font-medium text-xl text-black tracking-[-0.03em] leading-[125%]">
                {step.heading}
              </h3>
            </div>
            <p className="font-sans text-[15px] text-black/55 leading-relaxed mb-5 pl-11">
              {step.body}
            </p>
            <div className="w-full">{CARDS[i]}</div>
          </section>
        ))}
      </div>

      {/* CTA Buttons */}
      <div className="relative z-20 w-full max-w-[520px] lg:max-w-[1080px] mx-auto px-6 pt-0 pb-8 lg:pb-12 mt-10 lg:mt-[-48px] flex flex-col items-center gap-6">
        {showCtas ? (
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Link
              href="/pricing"
              className="inline-flex items-center rounded-full px-10 py-4 bg-black hover:bg-black/80 text-white transition-colors duration-300"
            >
              <span className="font-sans font-semibold text-lg leading-[27px]">
                See pricing
              </span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link
              href="/product"
              className="inline-flex items-center rounded-full px-10 py-4 border border-black/15 bg-white hover:bg-black/[0.04] text-black transition-colors duration-300"
            >
              <span className="font-sans font-semibold text-lg leading-[27px]">
                Learn more<span className="sr-only"> about LovedByAI</span>
              </span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
